import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import { Button } from '../components/ui/button';
import { ReportStatusBadge } from '../components/devreport/shared';
import ReportDocument from '../components/devreport/presentation/ReportDocument';
import { ShareReportDialog } from '../components/devreport/ShareReportDialog';
import { buildPresentation, pdfFilename } from '../components/devreport/presentation/presentation';
import { fmtDate } from '../components/devreport/constants';
import { ArrowLeft, Archive, ArchiveRestore, Copy, PenLine, Printer, Download, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevelopmentReportView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const isStaff = ['Admin', 'Staff'].includes(user?.role) || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'view');
  const canEdit = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'edit');
  const canArchive = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_ARCHIVE, 'edit');
  const canRestore = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_RESTORE, 'edit');
  const canDuplicate = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_DUPLICATE, 'edit');
  const canShare = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_SHARE, 'edit');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/development-reports/${reportId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      navigate('/development-reports');
    } finally {
      setLoading(false);
    }
  }, [reportId, navigate]);

  useEffect(() => { load(); }, [load]);

  const openPrint = () => window.open(`/development-reports/${reportId}/print`, '_blank');
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/development-reports/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const p = buildPresentation(data);
      a.download = pdfFilename(p.report, p.overview);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let detail = '';
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch (_) { /* noop */ }
      console.warn('[PDF] one-click download failed:', detail || e.message);
      toast.error(detail ? `PDF failed: ${detail}` : 'One-click PDF failed — opening Print / Save as PDF instead.');
      openPrint();
    } finally {
      setDownloading(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('Archive this report? It remains viewable but is clearly marked Archived.')) return;
    setArchiving(true);
    try {
      await axios.post(`${API}/development-reports/${reportId}/archive`);
      toast.success('Report archived');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      await axios.post(`${API}/development-reports/${reportId}/restore`);
      toast.success('Report restored');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const duplicate = async () => {
    setDuplicating(true);
    try {
      const res = await axios.post(`${API}/development-reports/${reportId}/duplicate`);
      toast.success('Duplicated as a new draft');
      navigate(`/development-reports/${res.data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  if (loading) return <Layout><div className="py-16 text-center text-slate-400">Loading report…</div></Layout>;
  if (!data) return null;

  const report = data.report;
  const isDraft = report.status === 'draft';
  const snapshot = data.snapshot || null;
  const canExport = user?.role === 'Admin' || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'view');
  const pres = buildPresentation(data);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="report-view">
      <div className="no-print sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {isStaff && <button onClick={() => navigate('/development-reports')} className="text-slate-400 hover:text-slate-700" data-testid="view-back"><ArrowLeft className="h-5 w-5" /></button>}
            <div>
              <h1 className="text-sm font-semibold text-slate-800">{report.athlete_name}</h1>
              <div className="text-xs text-slate-400">
                {report.status === 'finalized' && `Finalized ${fmtDate(report.finalized_at)}${snapshot?.finalized_by_name ? ' · ' + snapshot.finalized_by_name : ''}`}
                {report.status === 'archived' && 'Archived'}
                {isDraft && 'Draft'}
              </div>
            </div>
            <ReportStatusBadge status={report.status} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canShare && <ShareReportDialog reportId={reportId} />}
            {isDraft && canEdit && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/development-reports/${reportId}/edit`)} data-testid="view-open-builder">
                <PenLine className="mr-1.5 h-4 w-4" /> Open in Builder
              </Button>
            )}
            {!isDraft && (
              <>
                <Button variant="outline" size="sm" onClick={openPrint} data-testid="view-print">
                  <Printer className="mr-1.5 h-4 w-4" /> Print
                </Button>
                {canExport && (
                  <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="view-download-pdf">
                    {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download PDF
                  </Button>
                )}
              </>
            )}
            {!isDraft && canDuplicate && (
              <Button variant="outline" size="sm" onClick={duplicate} disabled={duplicating} data-testid="view-duplicate">
                {duplicating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Copy className="mr-1.5 h-4 w-4" />} Duplicate
              </Button>
            )}
            {report.status === 'finalized' && canArchive && (
              <Button variant="outline" size="sm" onClick={archive} disabled={archiving} data-testid="view-archive">
                <Archive className="mr-1.5 h-4 w-4" /> {archiving ? 'Archiving…' : 'Archive'}
              </Button>
            )}
            {report.status === 'archived' && canRestore && (
              <Button variant="outline" size="sm" onClick={restore} disabled={restoring} data-testid="view-restore">
                <ArchiveRestore className="mr-1.5 h-4 w-4" /> {restoring ? 'Restoring…' : 'Restore'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {report.status === 'archived' && (
        <div className="no-print mx-auto mt-4 max-w-5xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="archived-notice">
          This report is archived. It is preserved as a historical snapshot and cannot be edited.
        </div>
      )}

      <div className="py-6">
        <ReportDocument report={pres.report} sections={pres.sections} overview={pres.overview} lead={pres.lead} />
      </div>
    </div>
  );
};

export default DevelopmentReportView;
